import { useCallback, useEffect, useRef, useState } from "react";
import { parseFrame } from "../lib/live";
import type { MbLive } from "../lib/live";
import { isBoardSupported, openBoard } from "../lib/board";
import type { BoardLink } from "../lib/board";
import { isBleSupported, openBoardBle } from "../lib/ble";

export type BoardStatus = "unsupported" | "idle" | "connecting" | "on" | "failed";
/** 보드를 잇는 길 — USB(Web Serial) · 블루투스(Web Bluetooth · lib/ble) */
export type BoardVia = "usb" | "ble";

/**
 * 진짜 보드(4단계) — '🔌 진짜 보드 연결' 단추가 부른다. 연결되면 보드가 보내는 PF1 줄이 시뮬레이터 소식 대신 놀이터를 움직인다(MicrobitScreen).
 * `onFrame` 은 줄마다(흐르는 글자 · 출발 시각을 놓치지 않게 — useMicrobitLive 와 같다). AI 값은 `send` 로 보드에 한 줄.
 */
export function useBoard(onFrame?: (v: MbLive, at: number) => void,
  opts: { open?: typeof openBoard; openBle?: typeof openBoardBle; supported?: boolean; bleSupported?: boolean; onLine?: (line: string) => void } = {}) {
  const open = opts.open ?? openBoard;
  const openBle = opts.openBle ?? openBoardBle;
  const usb = opts.supported ?? isBoardSupported();
  const ble = opts.bleSupported ?? isBleSupported();
  const supported = usb || ble;
  const [status, setStatus] = useState<BoardStatus>(supported ? "idle" : "unsupported");
  const [via, setVia] = useState<BoardVia | null>(null);
  const [live, setLive] = useState<MbLive | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const link = useRef<BoardLink | null>(null);
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  /** 줄 그대로(라디오 광장 게이트웨이 — lib/gateway) */
  const onLineRef = useRef(opts.onLine);
  onLineRef.current = opts.onLine;

  const connect = useCallback(async (how: BoardVia = usb ? "usb" : "ble") => {
    if ((how === "usb" ? !usb : !ble) || link.current) return;
    setStatus("connecting");
    try {
      link.current = await (how === "usb" ? open : openBle)(
        (line) => {
          onLineRef.current?.(line);
          const frame = parseFrame(line);
          if (!frame) return;
          const v: MbLive = { projectId: null, frame };
          const now = Date.now();
          setLive(v);
          setAt(now);
          onFrameRef.current?.(v, now);
        },
        () => { link.current = null; setStatus("idle"); setVia(null); setLive(null); setAt(null); },
      );
      setVia(how);
      setStatus("on");
    } catch {
      link.current = null;
      setStatus("failed");
    }
  }, [open, openBle, usb, ble]);

  const disconnect = useCallback(async () => { await link.current?.close(); }, []);
  const send = useCallback((line: string) => { void link.current?.write(line); return !!link.current; }, []);
  useEffect(() => () => { void link.current?.close(); }, []);

  return { status, live, at, connect, disconnect, send, on: status === "on", via, usb, ble };
}
